import { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { AlertTriangle, CalendarDays, Hand, Loader2, MapPin, Package, Plus, Ticket, User } from "lucide-react";
import CustomerSelect from "@/components/CustomerSelect";
import DeleteButton from "@/components/DeleteButton";
import { FieldTimeline, MaterialRequestCard, MaterialsEditor, MaterialsList } from "@/components/dwr/TaskMaterials";
import {
  CATEGORY_LABEL, DELAY_LABEL, PRIORITY_LABEL, STATUS_LABEL, STATUS_TONE, TASK_STATUSES, fmtDate,
} from "@/lib/dwr";

const PRIORITY_TONE = { critical: "text-rose-600", high: "text-amber-600", medium: "text-foreground", low: "text-muted-foreground" };

function Field({ label, children, required }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs uppercase tracking-[0.16em]">{label}{required && <span className="text-rose-500 ml-1">*</span>}</Label>
      {children}
    </div>
  );
}

function Pick({ value, onChange, options, placeholder, disabled }) {
  return (
    <Select value={value || ""} onValueChange={onChange} disabled={disabled}>
      <SelectTrigger><SelectValue placeholder={placeholder} /></SelectTrigger>
      <SelectContent>{Object.entries(options).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
    </Select>
  );
}

const initials = (name = "") => name.split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0]).join("").toUpperCase() || "?";

// Each person's own color (users.avatar_color, fixed for good); grey for older data without one.
function StaffAvatar({ person, size = 36, className = "" }) {
  return (
    <span className={`inline-flex items-center justify-center rounded-full text-white font-semibold shrink-0 ${className}`}
      style={{ width: size, height: size, fontSize: size * 0.38, background: person.color || "#64748b" }} title={person.name}>
      {initials(person.name)}
    </span>
  );
}

// Click people to add or remove them; at least one always stays on the task.
function StaffPicker({ staff, value, onChange, disabled }) {
  const [q, setQ] = useState("");
  const shown = q ? staff.filter((s) => s.name.toLowerCase().includes(q.toLowerCase())) : staff;
  const toggle = (id) => {
    if (value.includes(id)) { if (value.length > 1) onChange(value.filter((x) => x !== id)); }
    else onChange([...value, id]);
  };
  return (
    <div className="space-y-2" data-testid="task-assignees">
      {staff.length > 12 && <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search staff" className="h-8" />}
      <div className="flex flex-wrap gap-2 max-h-48 overflow-y-auto">
        {shown.map((s) => {
          const on = value.includes(s.id);
          return (
            <button type="button" key={s.id} disabled={disabled} onClick={() => toggle(s.id)} aria-pressed={on}
              className={`flex items-center gap-2 pl-1 pr-3 py-1 rounded-full border text-sm transition-colors disabled:opacity-60 ${on ? "border-primary bg-primary/10 text-foreground" : "border-border text-muted-foreground hover:border-primary/40"}`}>
              <StaffAvatar person={s} size={26} className={on ? "ring-2 ring-primary ring-offset-1 ring-offset-background" : ""} />
              {s.name}
            </button>
          );
        })}
      </div>
    </div>
  );
}

const HISTORY = {
  created: "created", accepted: "accepted the task", materials: "changed the materials",
  materials_requested: "asked for a material change", materials_approved: "approved the material change",
  materials_rejected: "rejected the material change",
};

function TaskDialog({ task, staff, ticket, onClose, onSaved }) {
  const { user, dwr } = useAuth();
  const isNew = !task?.id;
  const [form, setForm] = useState(() => task?.id ? { ...task, materials: task.materials || [] } : {
    priority: "medium", category: ticket ? "support" : "other", status: "pending", assignees: [user?.id], materials: [],
    ...(ticket ? { title: ticket.subject || "", customer: ticket.customer || "" } : {}),
  });
  const [saving, setSaving] = useState(false);
  const [requests, setRequests] = useState([]);
  const loadRequests = useCallback(() => {
    if (isNew) return;
    api.get("/dwr/material-requests", { params: { status: "pending" } })
      .then(({ data }) => setRequests(data.items.filter((r) => r.task_id === task.id))).catch(() => setRequests([]));
  }, [isNew, task?.id]);
  useEffect(() => { loadRequests(); }, [loadRequests]);
  // A task with materials is completed from the phone, with what was used.
  const viaFlow = !isNew && task.materials?.length > 0 && task.status !== "completed";
  const set = (patch) => setForm((f) => ({ ...f, ...patch }));
  const team = new Set((dwr?.team || []).map((t) => t.id));
  const assignees = form.assignees || (form.assigned_to ? [form.assigned_to] : []);
  const canManage = isNew || dwr?.is_management || task.assigned_by === user?.id || (task.assignees || [task.assigned_to]).some((i) => team.has(i));
  // Everyone can open a task; only the people on it (or managing it) change it.
  const canUpdate = canManage || (task.assignees || [task.assigned_to]).includes(user?.id);

  const save = async () => {
    if (!form.title?.trim()) { toast.error("Give the task a title"); return; }
    if (form.status === "delayed" && (!form.delay_reason || !form.delay_responsible?.trim())) {
      toast.error("A delayed task needs a reason and who must act next"); return;
    }
    setSaving(true);
    try {
      const fields = canManage
        ? ["title", "description", "category", "customer", "project", "location", "assignees", "deadline", "priority",
          "status", "supervisor_remarks", "delay_reason", "delay_responsible", "delay_note", "work_order"]
        : ["status", "delay_reason", "delay_responsible", "delay_note", "progress_note"];
      const payload = Object.fromEntries(fields.filter((k) => form[k] !== undefined).map((k) => [k, form[k]]));
      if (canManage) payload.assignees = assignees;
      if (canManage && form.status !== "completed" && task?.status !== "completed") {
        payload.materials = (form.materials || []).filter((m) => m.name?.trim()).map((m) => ({ ...m, qty: Number(m.qty) || 0 }));
      }
      if (isNew && ticket) payload.ticket_id = ticket.id;
      const { data } = isNew ? await api.post("/dwr/tasks", payload) : await api.put(`/dwr/tasks/${task.id}`, payload);
      toast.success(isNew ? `Task ${data.task_no} created` : "Task updated");
      onSaved(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display">{isNew ? "New task" : `${task.task_no} · ${task.title}`}</DialogTitle>
          <DialogDescription>
            {isNew ? (ticket ? `From support ticket ${ticket.ticket_no}. The technician accepts it on their phone.` : "Assign work with a deadline, priority and the materials it needs.")
              : `Assigned to ${task.assigned_to_name} by ${task.assigned_by_name}${task.ticket_no ? ` · ticket ${task.ticket_no}` : ""}`}
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 py-2">
          {!isNew && (
            <div className="sm:col-span-2 space-y-3">
              <FieldTimeline task={task} />
              {!task.accepted && task.status !== "cancelled" && <p className="text-xs text-amber-600">Waiting for the technician to accept.</p>}
            </div>
          )}
          <div className="sm:col-span-2"><Field label="Task" required>
            <Input value={form.title || ""} disabled={!canManage} onChange={(e) => set({ title: e.target.value })} data-testid="task-title" />
          </Field></div>
          <div className="sm:col-span-2"><Field label="Description">
            <Textarea rows={3} value={form.description || ""} disabled={!canManage} onChange={(e) => set({ description: e.target.value })} />
          </Field></div>
          <div className="sm:col-span-2"><Field label={`Assigned to${assignees.length > 1 ? ` · ${assignees.length} people` : ""}`} required>
            <StaffPicker staff={staff} value={assignees} disabled={!canManage} onChange={(v) => set({ assignees: v })} />
          </Field></div>
          <Field label="Deadline">
            <Input type="date" value={form.deadline || ""} disabled={!canManage} onChange={(e) => set({ deadline: e.target.value })} />
          </Field>
          <Field label="Priority"><Pick value={form.priority} disabled={!canManage} onChange={(v) => set({ priority: v })} options={PRIORITY_LABEL} /></Field>
          <Field label="Type of work"><Pick value={form.category} disabled={!canManage} onChange={(v) => set({ category: v })} options={CATEGORY_LABEL} /></Field>
          <Field label="Customer">
            <CustomerSelect value={form.customer || ""} disabled={!canManage} onChange={(v) => set({ customer: v })} testId="task-customer" />
          </Field>
          <Field label="Location"><Input value={form.location || ""} disabled={!canManage} onChange={(e) => set({ location: e.target.value })} /></Field>
          <div className="sm:col-span-2 space-y-2"><Field label={`Materials needed${form.materials?.length ? ` · ${form.materials.length}` : ""}`}>
            {canManage && task?.status !== "completed"
              ? <MaterialsEditor value={form.materials || []} onChange={(materials) => set({ materials })} />
              : <MaterialsList materials={form.materials} />}
          </Field>
            {form.required_materials && <p className="text-xs text-muted-foreground">Earlier notes: {form.required_materials}</p>}
            {requests.map((r) => <MaterialRequestCard key={r.id} request={r} onDecided={async () => {
              loadRequests();
              const { data } = await api.get(`/dwr/tasks/${task.id}`).catch(() => ({ data: null }));
              if (data) set({ materials: data.materials || [] });
              onSaved(null, true);
            }} />)}
          </div>
          <Field label="Linked work order"><Input value={form.work_order || ""} disabled={!canManage} onChange={(e) => set({ work_order: e.target.value })} placeholder="e.g. WO-00012" /></Field>
          <Field label="Status">
            <Select value={form.status} onValueChange={(v) => set({ status: v })} disabled={!canUpdate}>
              <SelectTrigger data-testid="task-status"><SelectValue /></SelectTrigger>
              <SelectContent>{TASK_STATUSES.filter((s) => (canManage || s !== "cancelled") && !(viaFlow && s === "completed")).map((s) => <SelectItem key={s} value={s}>{STATUS_LABEL[s]}</SelectItem>)}</SelectContent>
            </Select>
            {viaFlow && <p className="text-[11px] text-muted-foreground mt-1">Completed from the phone, with the materials used.</p>}
          </Field>
          {form.status === "delayed" && (
            <div className="sm:col-span-2 grid grid-cols-1 sm:grid-cols-2 gap-4 p-3 rounded-lg bg-rose-500/5 border border-rose-500/30">
              <Field label="Reason for delay" required><Pick value={form.delay_reason} onChange={(v) => set({ delay_reason: v })} options={DELAY_LABEL} placeholder="Choose a reason" /></Field>
              <Field label="Who must act next" required>
                <Input value={form.delay_responsible || ""} onChange={(e) => set({ delay_responsible: e.target.value })} placeholder="Person, team or company" />
              </Field>
              <div className="sm:col-span-2"><Field label="Delay details">
                <Textarea rows={2} value={form.delay_note || ""} onChange={(e) => set({ delay_note: e.target.value })} />
              </Field></div>
            </div>
          )}
          {!isNew && !canManage && canUpdate && (
            <div className="sm:col-span-2"><Field label="Progress note">
              <Textarea rows={2} value={form.progress_note || ""} onChange={(e) => set({ progress_note: e.target.value })} />
            </Field></div>
          )}
          {canManage && !isNew && (
            <div className="sm:col-span-2"><Field label="Supervisor remarks">
              <Textarea rows={2} value={form.supervisor_remarks || ""} onChange={(e) => set({ supervisor_remarks: e.target.value })} />
            </Field></div>
          )}
          {!isNew && task.history?.length > 0 && (
            <div className="sm:col-span-2">
              <Label className="text-xs uppercase tracking-[0.16em]">History</Label>
              <ul className="mt-2 space-y-1 text-xs text-muted-foreground">
                {[...task.history].reverse().map((h, i) => (
                  <li key={i}>{new Date(h.at).toLocaleString("en-GB")} · {h.by} · {h.action === "status" ? `${STATUS_LABEL[h.from] || h.from} → ${STATUS_LABEL[h.to] || h.to}` : h.action === "reassigned" ? `reassigned to ${h.to}` : HISTORY[h.action] || h.action}{h.note ? ` (${h.note})` : ""}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
        <DialogFooter>
          {!isNew && <DeleteButton endpoint={`/dwr/tasks/${task.id}`} what="task" onDeleted={() => onSaved(null)} />}
          <Button variant="ghost" onClick={onClose}>{canUpdate ? "Cancel" : "Close"}</Button>
          {canUpdate && <Button onClick={save} disabled={saving} className="kn-gradient-bg text-white border-0" data-testid="task-save">
            {saving && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}{isNew ? "Create task" : "Save"}
          </Button>}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function Tasks() {
  const { dwr } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [ticket, setTicket] = useState(null);
  const [scope, setScope] = useState("mine");
  const [status, setStatus] = useState("open");
  const [items, setItems] = useState(null);
  const [staff, setStaff] = useState([]);
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    setItems(null);
    try {
      const params = { scope };
      if (status !== "open" && status !== "overdue") params.status = status;
      if (status === "overdue") params.overdue = true;
      const { data } = await api.get("/dwr/tasks", { params });
      setItems(status === "open" ? data.items.filter((t) => !["completed", "cancelled"].includes(t.status)) : data.items);
    } catch (e) {
      toast.error(formatApiError(e));
      setItems([]);
    }
  }, [scope, status]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/dwr/staff").then(({ data }) => setStaff(data.items || [])).catch(() => {}); }, []);

  // Arriving from "Create Task" on a support ticket: open New Task from it.
  useEffect(() => {
    const t = location.state?.ticketForTask;
    if (!t) return;
    setTicket(t);
    setEditing({});
    navigate(location.pathname, { replace: true, state: {} });
  }, [location.state]); // eslint-disable-line react-hooks/exhaustive-deps

  const tabs = [["mine", "Mine"], ["assigned_by_me", "Assigned by me"]];
  if (dwr?.is_supervisor) tabs.push(["team", "My team"]);
  if (dwr?.is_management) tabs.push(["all", "All"]);

  return (
    <div className="space-y-5" data-testid="tasks-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-display text-3xl font-semibold tracking-tight">Tasks</h1>
          <p className="text-sm text-muted-foreground mt-1">Assigned work, deadlines and what's holding things up.</p>
        </div>
        <Button onClick={() => setEditing({})} className="kn-gradient-bg text-white border-0" data-testid="new-task">
          <Plus className="w-4 h-4 mr-1" /> New task
        </Button>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <Tabs value={scope} onValueChange={setScope}>
          <TabsList>{tabs.map(([k, l]) => <TabsTrigger key={k} value={k}>{l}</TabsTrigger>)}</TabsList>
        </Tabs>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="w-48"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="open">Open tasks</SelectItem>
            <SelectItem value="overdue">Overdue</SelectItem>
            <SelectItem value="all">All statuses</SelectItem>
            {TASK_STATUSES.map((s) => <SelectItem key={s} value={s}>{STATUS_LABEL[s]}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {items === null ? (
        <div className="py-16 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>
      ) : items.length === 0 ? (
        <Card className="p-8 text-center border-dashed">
          <p className="font-medium">No tasks here</p>
          <p className="text-sm text-muted-foreground mt-1">Click "New task" to assign work to yourself{dwr?.is_supervisor || dwr?.is_management ? " or your team" : ""}.</p>
        </Card>
      ) : (
        <div className="grid gap-3">
          {items.map((t) => (
            <Card key={t.id} role="button" tabIndex={0} onClick={() => setEditing(t)} onKeyDown={(e) => e.key === "Enter" && setEditing(t)}
              className={`p-4 rounded-xl border bg-card cursor-pointer hover:border-primary/40 transition-colors ${t.overdue ? "border-rose-500/40" : "border-border"}`}
              data-testid={`task-${t.task_no}`}>
              <div className="flex flex-wrap items-start justify-between gap-2">
                <div className="min-w-0">
                  <p className="text-xs font-mono text-muted-foreground">{t.task_no} · <span className={PRIORITY_TONE[t.priority]}>{PRIORITY_LABEL[t.priority]} priority</span> · {CATEGORY_LABEL[t.category] || "Other"}</p>
                  <p className="font-medium mt-0.5">{t.title}</p>
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {!t.accepted && !["completed", "cancelled"].includes(t.status) && (
                    <span className="text-xs font-medium px-2.5 py-1 rounded-full border border-amber-500/40 text-amber-700 dark:text-amber-400 inline-flex items-center gap-1"><Hand className="w-3 h-3" />Awaiting acceptance</span>
                  )}
                  {t.arrived_at && !t.completed_at && (
                    <span className="text-xs font-medium px-2.5 py-1 rounded-full border border-sky-500/40 text-sky-700 dark:text-sky-400 inline-flex items-center gap-1"><MapPin className="w-3 h-3" />On site</span>
                  )}
                  <span className={`text-xs font-medium px-2.5 py-1 rounded-full border ${STATUS_TONE[t.status]}`}>{STATUS_LABEL[t.status]}</span>
                </div>
              </div>
              <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2 text-xs text-muted-foreground">
                <span className="inline-flex items-center gap-1.5">
                  {t.assignee_people?.length
                    ? <span className="flex -space-x-1.5">{t.assignee_people.slice(0, 4).map((p) => <StaffAvatar key={p.id} person={p} size={20} className="ring-2 ring-card" />)}</span>
                    : <User className="w-3.5 h-3.5" />}
                  {t.assigned_to_name}
                </span>
                {t.customer && <span>{t.customer}</span>}
                {t.materials?.length > 0 && <span className="inline-flex items-center gap-1"><Package className="w-3.5 h-3.5" />{t.materials.length} material{t.materials.length > 1 ? "s" : ""}</span>}
                {t.ticket_no && <span className="inline-flex items-center gap-1"><Ticket className="w-3.5 h-3.5" />{t.ticket_no}</span>}
                {t.location && <span className="inline-flex items-center gap-1"><MapPin className="w-3.5 h-3.5" />{t.location}</span>}
                {t.deadline && (
                  <span className={`inline-flex items-center gap-1 ${t.overdue ? "text-rose-600 font-medium" : ""}`}>
                    {t.overdue ? <AlertTriangle className="w-3.5 h-3.5" /> : <CalendarDays className="w-3.5 h-3.5" />}
                    {t.overdue ? "Overdue: " : "Due "}{fmtDate(t.deadline)}
                  </span>
                )}
              </div>
              {t.status === "delayed" && (
                <p className="mt-2 text-xs text-rose-700 dark:text-rose-400">
                  {DELAY_LABEL[t.delay_reason] || "Delayed"} · next action: {t.delay_responsible}
                </p>
              )}
            </Card>
          ))}
        </div>
      )}

      {editing && (
        <TaskDialog task={editing} staff={staff} ticket={editing.id ? null : ticket}
          onClose={() => { setEditing(null); setTicket(null); }}
          onSaved={(_, keepOpen) => { if (!keepOpen) { setEditing(null); setTicket(null); } load(); }} />
      )}
    </div>
  );
}
